'use client';

import { useState } from 'react';
import { toast } from 'sonner';
import { Instagram, Linkedin, ExternalLink } from 'lucide-react';
import { Header } from '../../Header';
import { Footer } from '../../Footer';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { RedditIcon, DiscordIcon } from '../../icons/BrandIcons';
import { PageType, HUSTLANCE_URL, HUSTLANCE_INSTAGRAM_URL, HUSTLANCE_REDDIT_URL, HUSTLANCE_DISCORD_URL, HUSTLANCE_LINKEDIN_URL } from '../../Router';
import { createContactMessage } from '../../../firebase/blog-db';
import { sendEmail } from '../../../lib/email';
import { COUNTRY_CODES } from '../../../lib/countryCodes';

interface BlogContactPageProps {
  onNavigate: (page: PageType) => void;
}

// Confirmed recipients. The shared EmailJS template has no configured CC
// field (that lives in the EmailJS dashboard, which this codebase can't
// inspect), so each address gets its own send rather than relying on a CC
// header — functionally identical from the recipients' side.
const CONTACT_NOTIFY_EMAILS = ['nash.edu2004@gmail.com', '321blazerkai2.o@gmail.com', 'amudhan.oficial@gmail.com'];

const MANUAL_CODE = '__manual__';

const EMPTY_FORM = { firstName: '', lastName: '', email: '', countryIso: 'US', manualCode: '', phone: '', message: '' };

export function BlogContactPage({ onNavigate }: BlogContactPageProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.phone && form.phone.length !== 10) {
      toast.error('Phone number must be exactly 10 digits.');
      return;
    }
    setSubmitting(true);
    const dialCode = form.countryIso === MANUAL_CODE
      ? (form.manualCode.startsWith('+') ? form.manualCode : `+${form.manualCode}`)
      : COUNTRY_CODES.find(c => c.iso === form.countryIso)?.code || '';
    const fullPhone = form.phone ? `${dialCode} ${form.phone}` : '';
    try {
      await createContactMessage({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: fullPhone,
        message: form.message,
      });
      setSent(true);
      setForm(EMPTY_FORM);
      toast.success("Message sent — we'll get back to you soon.");
      // Best-effort — the message is already safely stored in Firestore
      // (visible at /admin/messages) even if these emails fail to send.
      for (const notifyEmail of CONTACT_NOTIFY_EMAILS) {
        sendEmail({
          to_email: notifyEmail,
          from_name: `${form.firstName} ${form.lastName}`.trim(),
          from_email: form.email,
          subject: `New Hustlance Blog contact message from ${form.firstName}`,
          message: `${form.message || '(no message)'}\n\nFrom: ${form.firstName} ${form.lastName} <${form.email}>${fullPhone ? `\nPhone: ${fullPhone}` : ''}`,
        }).catch(() => {});
      }
    } catch {
      toast.error('Could not send your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header onNavigate={onNavigate} />
      <div className="pt-14">
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-14">
            <div>
              <h1 className="font-blocky text-3xl sm:text-4xl text-hustlance-primary leading-tight mb-8">
                Looking for more information? Get in touch with us today.
              </h1>

              <div className="grid grid-cols-2 gap-6 pb-6 mb-6 border-b border-gray-200">
                <div>
                  <p className="font-bold text-gray-900 mb-1">Our Website</p>
                  <a href={HUSTLANCE_URL} className="text-gray-500 hover:text-hustlance-primary transition-colors inline-flex items-center gap-1">
                    hustlance.com <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
                <div>
                  <p className="font-bold text-gray-900 mb-1">Follow</p>
                  <div className="flex items-center gap-3 text-gray-500">
                    <a href={HUSTLANCE_INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Hustlance on Instagram" className="hover:text-hustlance-primary transition-colors">
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a href={HUSTLANCE_REDDIT_URL} target="_blank" rel="noopener noreferrer" aria-label="Hustlance on Reddit" className="hover:text-hustlance-primary transition-colors">
                      <RedditIcon className="w-5 h-5" />
                    </a>
                    <a href={HUSTLANCE_DISCORD_URL} target="_blank" rel="noopener noreferrer" aria-label="Hustlance on Discord" className="hover:text-hustlance-primary transition-colors">
                      <DiscordIcon className="w-5 h-5" />
                    </a>
                    <a href={HUSTLANCE_LINKEDIN_URL} target="_blank" rel="noopener noreferrer" aria-label="Hustlance on LinkedIn" className="hover:text-hustlance-primary transition-colors">
                      <Linkedin className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>

              <p className="text-gray-500 leading-relaxed">
                Have a question about an article, want to pitch a story, or found something that needs fixing?
                Send us a message and the Hustlance Blog team will get back to you.
              </p>
            </div>

            <div>
              {sent ? (
                <div className="bg-hustlance-background rounded-lg p-8 text-center">
                  <p className="font-bold text-gray-900 mb-1">Thanks for reaching out!</p>
                  <p className="text-gray-500">We've received your message and will respond soon.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="firstName">First name *</Label>
                      <Input id="firstName" required value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last name *</Label>
                      <Input id="lastName" required value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} className="mt-1.5" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <div className="mt-1.5 flex gap-2">
                      <Select value={form.countryIso} onValueChange={iso => setForm({ ...form, countryIso: iso })}>
                        <SelectTrigger className="w-40 shrink-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {COUNTRY_CODES.map(c => (
                            <SelectItem key={c.iso} value={c.iso}>{c.name} ({c.code})</SelectItem>
                          ))}
                          <SelectItem value={MANUAL_CODE}>Enter code manually…</SelectItem>
                        </SelectContent>
                      </Select>
                      {form.countryIso === MANUAL_CODE ? (
                        <Input
                          type="text"
                          inputMode="tel"
                          placeholder="+xx"
                          className="w-20 shrink-0"
                          value={form.manualCode}
                          onChange={e => setForm({ ...form, manualCode: e.target.value.replace(/[^\d+]/g, '').slice(0, 5) })}
                        />
                      ) : null}
                      <Input
                        id="phone"
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="10-digit number"
                        value={form.phone}
                        onChange={e => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea id="message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="mt-1.5 min-h-32" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-hustlance-primary hover:opacity-90 text-white">
                    {submitting ? 'Sending...' : 'Submit'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </main>
      </div>
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
